/**
 * State Visualization Component
 * 
 * Displays current state and available transitions from server.
 * Shows state history and provides visual feedback for state machine status.
 */

import React from 'react';
import { Card, Badge } from '@etherisc/ui-kit';

export interface StateVisualizationProps {
  processKey: string;
  currentState: string;
  nextEvents: string[];
  businessKey: string;
  version: number;
  history?: Array<{
    event: string;
    fromState: string;
    toState: string;
    at: Date;
    actorId?: string;
  }>;
  className?: string;
}

export const StateVisualization: React.FC<StateVisualizationProps> = ({
  processKey,
  currentState,
  nextEvents,
  businessKey,
  version,
  history = [],
  className = ''
}) => {
  const getStateDisplayName = (state: string): string => {
    return state.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStateColor = (state: string): string => {
    switch (state) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'ready_for_payment_collection': return 'bg-yellow-100 text-yellow-800';
      case 'partial_payment_received': return 'bg-orange-100 text-orange-800';
      case 'activated': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'payment_collection_expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleString();
  };

  return (
    <Card className={className}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {processKey.charAt(0).toUpperCase() + processKey.slice(1)} State
          </h3>
          <Badge variant="secondary">v{version}</Badge>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="text-sm text-gray-600 mb-1">Business Key:</div>
            <div className="font-mono text-sm bg-gray-50 p-2 rounded">{businessKey}</div>
          </div>

          <div>
            <div className="text-sm text-gray-600 mb-2">Current State:</div>
            <Badge className={getStateColor(currentState)}>
              {getStateDisplayName(currentState)}
            </Badge>
          </div>
        </div>

        {nextEvents.length > 0 && (
          <div className="mb-6">
            <div className="text-sm text-gray-600 mb-2">Available Actions:</div>
            <div className="flex flex-wrap gap-2">
              {nextEvents.map((event) => (
                <Badge
                  key={event}
                  variant="outline"
                  className="text-xs"
                >
                  {event.toLowerCase().replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div>
            <div className="text-sm text-gray-600 mb-3">Recent History:</div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {history.slice(0, 5).map((entry, index) => (
                <div key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">
                      {entry.event}
                    </Badge>
                    <span className="text-gray-600">
                      {getStateDisplayName(entry.fromState)} → {getStateDisplayName(entry.toState)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(entry.at)}
                  </div>
                </div>
              ))}
              {history.length > 5 && (
                <div className="text-xs text-gray-500 text-center py-2">
                  ... and {history.length - 5} more transitions
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
